import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Lock, ArrowLeft, ArrowRight, Loader2, CheckCircle, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { authAPI } from '../lib/api';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token');
  
  const [stage, setStage] = useState(tokenFromUrl ? 'reset' : 'request');
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState(tokenFromUrl || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      const response = await authAPI.forgotPassword({ email });
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
        setStage('token');
        toast.success('Reset token generated');
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetToken || !newPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await authAPI.resetPassword({ token: resetToken, new_password: newPassword });
      setStage('success');
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <Card className="w-full max-w-md border-0 shadow-[0_8px_30px_rgb(0,0,0,0.06)]" data-testid="forgot-password-card">
        <CardHeader className="space-y-1 pb-4 text-center">
          <div className="mx-auto mb-3 h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
            <KeyRound className="h-7 w-7 text-primary" />
          </div>
          <CardTitle className="font-heading text-xl sm:text-2xl">
            {stage === 'request' && 'Forgot Password'}
            {stage === 'token' && 'Reset Token Generated'}
            {stage === 'reset' && 'Set New Password'}
            {stage === 'success' && 'Password Reset'}
          </CardTitle>
          <CardDescription className="text-sm">
            {stage === 'request' && 'Enter your registered email to reset your password'}
            {stage === 'token' && 'Use the token below to reset your password'}
            {stage === 'reset' && 'Enter your new password'}
            {stage === 'success' && 'Your password has been updated'}
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 sm:px-6">
          {stage === 'request' && (
            <form onSubmit={handleRequestReset} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    data-testid="forgot-email-input"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12"
                  />
                </div>
              </div>
              <Button
                type="submit"
                data-testid="request-reset-btn"
                className="w-full h-12 rounded-full font-semibold"
                disabled={loading}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRight className="h-4 w-4 mr-2" />}
                {loading ? 'Processing...' : 'Reset Password'}
              </Button>
            </form>
          )}

          {stage === 'token' && (
            <div className="space-y-5">
              <div className="bg-muted rounded-lg p-4 text-center">
                <p className="text-xs text-muted-foreground mb-2">Your reset token:</p>
                <p className="font-mono text-sm break-all font-bold select-all" data-testid="reset-token-display">{resetToken}</p>
              </div>
              <Button
                onClick={() => setStage('reset')}
                data-testid="proceed-reset-btn"
                className="w-full h-12 rounded-full font-semibold"
              >
                <ArrowRight className="h-4 w-4 mr-2" />
                Set New Password
              </Button>
            </div>
          )}

          {stage === 'reset' && (
            <form onSubmit={handleResetPassword} className="space-y-5">
              {!tokenFromUrl && (
                <div className="space-y-2">
                  <Label htmlFor="token" className="text-sm font-medium">Reset Token</Label>
                  <Input
                    id="token"
                    data-testid="reset-token-input"
                    placeholder="Paste your reset token"
                    value={resetToken}
                    onChange={(e) => setResetToken(e.target.value)}
                    className="h-12 font-mono text-sm"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-sm font-medium">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="new-password"
                    type="password"
                    data-testid="new-password-input"
                    placeholder="Min 6 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10 h-12"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-sm font-medium">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="confirm-password"
                    type="password"
                    data-testid="confirm-password-input"
                    placeholder="Re-enter password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 h-12"
                  />
                </div>
              </div>
              <Button
                type="submit"
                data-testid="submit-reset-btn"
                className="w-full h-12 rounded-full font-semibold"
                disabled={loading}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          )}

          {stage === 'success' && (
            <div className="space-y-5 text-center">
              <div className="mx-auto h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-sm text-muted-foreground">You can now login with your new password.</p>
              <Button
                onClick={() => navigate('/admin/login')}
                data-testid="go-to-login-btn"
                className="w-full h-12 rounded-full font-semibold"
              >
                <ArrowRight className="h-4 w-4 mr-2" />
                Go to Login
              </Button>
            </div>
          )}

          {stage !== 'success' && (
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => navigate('/admin/login')}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
              >
                <ArrowLeft className="h-3 w-3" />
                Back to Login
              </button>
            </div>
          )}
        </CardContent>
      </Card>
      <div className="mt-6 text-center" data-testid="global-footer">
        <p className="text-xs text-muted-foreground/70">Powered by HotelPulse.in | All Rights Reserved &copy; 2026</p>
        <p className="text-xs text-muted-foreground/50 mt-0.5">For support, connect at info@hotelpulse.in</p>
      </div>
    </div>
  );
};

export default ForgotPassword;
